// Third-party packages.
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import * as QueryString from "query-string";

// Components
import StatisticsComparisonChart from "./statistics-comparison-chart";
import StatementsTable from "./statements-table";
import Breadcrumb from "../../breadcrumb";
import HeaderActions from "../../header_actions";
import PageContent from "../../page_content"
import ComparisonOptionsCard from "../comparison-options-card";
import StatisticsResultsTable from "./statistics-results-table";
import ConditionalRender from "../../../helpers/ConditionalRender";

// Types
import { StatisticsComparisonProps, StatsComparisonOptions } from "./types";
import InstanceTarget from "../../../types/instance/InstanceTarget";

// Context
import { InstanceContext } from "../../../context/InstanceContext";

// Helpers
import { getComparisonInitialOptions, resetComparisonInitialOptions } from "../defaultValues";
import { formatPeriod, getTimeZone } from "../../../helpers/utils";

const StatisticsComparison = (props: StatisticsComparisonProps) => {
    const { instances } = useContext(InstanceContext);

    const statsComparisonInitialOptions: StatsComparisonOptions = getComparisonInitialOptions(instances, true)

    const [formattedBreadCrumb, setFormattedBreadCrumb] = useState("");
    const navigate = useNavigate();
    const timezone = getTimeZone()

    const [options, setOptions] = useState<StatsComparisonOptions>(statsComparisonInitialOptions)
    const [generateId, setGenerateId] = useState<number>(0)
    const [statementColors, setStatementColors] = useState<Object>({})
    const [showResultsTable, setShowResultsTable] = useState<boolean>(false)

    const onOptionChange = (optionName: string, value: any) => {
        setOptions({ ...options, [optionName]: value })
    }

    const updateUrlParams = (type: string, value: string) => {
        const query = window.location.search;
        let queryString = QueryString.parse(query);
        queryString[type] = value
        navigate(`?${QueryString.stringify(queryString)}`)
    }

    const availableInstances = useMemo(() => {
        return instances
                .filter((instance: InstanceTarget) => instance.collectsqlstatistics)
                .sort((a, b) => a.name.localeCompare(b.name));
    }, [instances]);

    useEffect(() => {
        if (generateId) {
            updateUrlParams('metric', options.metric)
            updateUrlParams('chart', options.chart)
            updateUrlParams('topN', options.topN.toString())
            updateUrlParams('other', options.other.toString())
            updateUrlParams('showSqlId', options.showSqlId.toString())
            updateUrlParams('firstInstanceId', options.firstInstanceOptions.instanceId.toString())
            updateUrlParams('firstInstanceFrom', options.firstInstanceOptions.periodStart.toISOString())
            updateUrlParams('firstInstanceTo', options.firstInstanceOptions.periodEnd.toISOString())
            updateUrlParams('secondInstanceId', options.secondInstanceOptions.instanceId.toString())
            updateUrlParams('secondInstanceFrom', options.secondInstanceOptions.periodStart.toISOString())
            updateUrlParams('secondInstanceTo', options.secondInstanceOptions.periodEnd.toISOString())
            updateUrlParams('customId', options.customSqlId.toString())
            if (options.tagName) {
                updateUrlParams('tagName', options.tagName)
            }
            if (options.firstInstanceOptions.snapshotId) {
                updateUrlParams('firstInstanceSnapshotId', options.firstInstanceOptions.snapshotId.toString())
            }
            if (options.secondInstanceOptions.snapshotId) {
                updateUrlParams('secondInstanceSnapshotId', options.secondInstanceOptions.snapshotId.toString())
            }
        }
    }, [generateId])

    useEffect(() => {
        setOptions({
            ...options,
            showSqlId: options.firstInstanceOptions.instanceType === 'oracle' || options.secondInstanceOptions.instanceType === 'oracle'
        })
    }, [options.firstInstanceOptions, options.secondInstanceOptions])

    const onGenerate = () => {
        setShowResultsTable(true)
        setGenerateId(new Date().getTime())
    }

    useEffect(() => {
        if (options.firstInstanceOptions.instanceId && options.secondInstanceOptions.instanceId && options.metric && options.topN && options.firstInstanceOptions.periodStart && options.firstInstanceOptions.periodEnd && options.firstInstanceOptions.instanceType && options.secondInstanceOptions.instanceType) {
            setShowResultsTable(true)
            setGenerateId(new Date().getTime())
        }
    }, [options.firstInstanceOptions.instanceType, options.secondInstanceOptions.instanceType])

    const resetFilters = () => {
        const defaultValues = resetComparisonInitialOptions(true)
        setOptions(defaultValues);
    }

    useEffect(() => {
        if (!options?.firstInstanceOptions || !options?.secondInstanceOptions) {
            setFormattedBreadCrumb("");
            return;
        }

        const firstFormatted = formatPeriod(options.firstInstanceOptions);
        const secondFormatted = formatPeriod(options.secondInstanceOptions);

        setFormattedBreadCrumb(`${firstFormatted} / ${secondFormatted} (${timezone})`);
    }, [generateId]);

    return <React.Fragment>
        <PageContent title='SQL Stats Comparison - DBmarlin' description={''}>
            {/* Heading */}
            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb heading="SQL Stats Comparison">
                    <Link to="#">Reports</Link>
                    <NavLink to="/reports/sql-stats-comparison" className={"current-breadcrumb-link"}>SQL Stats Comparison</NavLink>
                    <span>{formattedBreadCrumb}</span>

                </Breadcrumb>
               <HeaderActions toggleMenu={props.toggleMenu}/>
            </div>
            <div className="row row-cols-1">
                <div className="col">
                    <ComparisonOptionsCard loading={false} options={options} type='sql-activity'
                                           resetFilters={resetFilters}
                                           instances={availableInstances}
                                           onOptionsChange={onOptionChange} onGenerate={onGenerate}/>
                    <ConditionalRender if={showResultsTable}>
                        <StatisticsResultsTable
                            instanceOptions={[options.firstInstanceOptions, options.secondInstanceOptions]}
                            options={options} generateId={generateId}/>
                    </ConditionalRender>
                    <ConditionalRender if={showResultsTable}>
                        <StatisticsComparisonChart
                            instanceOptions={[options.firstInstanceOptions, options.secondInstanceOptions]}
                            options={options} generateId={generateId} setStatementColors={setStatementColors}/>
                    </ConditionalRender>
                    <ConditionalRender if={showResultsTable}>
                        <StatementsTable instanceOptions={[options.firstInstanceOptions, options.secondInstanceOptions]}
                                         options={options} generateId={generateId} statementColors={statementColors}/>
                    </ConditionalRender>
                </div>
            </div>
        </PageContent>
    </React.Fragment>
};

export default StatisticsComparison;
